/**
 * The request contract every tenant-purge endpoint shares, in one place, so
 * the five services and the Dashboard runner that calls them cannot drift on
 * what a tenant id looks like or what an omitted `dryRun` means.
 *
 *   POST .../tenants/:tenantId/purge   body { dryRun?: boolean }
 *
 * `dryRun` omitted means dry run, so a hand-typed curl cannot erase anything
 * by accident; a live run has to say `{ "dryRun": false }` out loud. A present
 * but non-boolean value is rejected rather than coerced — `"false"` must not
 * silently become either answer.
 *
 * See apps/dashboard/docs/settings-open-items.md §7.
 */

export const TENANT_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export interface TenantPurgeRequest {
  tenantId: string;
  dryRun: boolean;
  /**
   * Count an organization that is NOT deleted yet. Only ever true on a dry run
   * — see `INVALID_ALLOW_NOT_DELETED` below for why that is enforced here and
   * not left to callers.
   *
   * It exists for one screen: the delete-organization modal shows the owner
   * what they are about to lose BEFORE they confirm, and at that moment the
   * organization is still live, so every service's second factor would
   * otherwise refuse to count it.
   *
   * Named for what it relaxes, not "allowLive" — "live" already means "a live
   * run, not a dry run" everywhere else in this contract, and one word meaning
   * both would be exactly the wrong ambiguity in this file.
   */
  allowNotDeleted: boolean;
}

export type TenantPurgeRequestErrorCode =
  | 'INVALID_TENANT_ID'
  | 'INVALID_DRY_RUN'
  | 'INVALID_ALLOW_NOT_DELETED';

export type TenantPurgeRequestParse =
  | { ok: true; value: TenantPurgeRequest }
  | { ok: false; code: TenantPurgeRequestErrorCode; message: string };

export function parseTenantPurgeRequest(tenantId: unknown, body: unknown): TenantPurgeRequestParse {
  if (typeof tenantId !== 'string' || !TENANT_ID_PATTERN.test(tenantId)) {
    return { ok: false, code: 'INVALID_TENANT_ID', message: 'tenantId must match ^[A-Za-z0-9_-]{1,64}$' };
  }
  const asObject = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const raw = asObject.dryRun;
  if (raw !== undefined && typeof raw !== 'boolean') {
    return { ok: false, code: 'INVALID_DRY_RUN', message: 'dryRun must be a boolean when present' };
  }
  const dryRun = raw === undefined ? true : raw;

  const rawAllow = asObject.allowNotDeleted;
  if (rawAllow !== undefined && typeof rawAllow !== 'boolean') {
    return {
      ok: false,
      code: 'INVALID_ALLOW_NOT_DELETED',
      message: 'allowNotDeleted must be a boolean when present',
    };
  }
  // The one rule that makes this flag safe to have at all: it can never travel
  // with a live run. A request that asks to erase an organization that is not
  // deleted is refused here, before any service reads it — so the flag cannot
  // widen erasure even if a caller is confused, compromised, or hand-typed.
  // The services check the same thing again; this is the outer of three.
  if (rawAllow === true && !dryRun) {
    return {
      ok: false,
      code: 'INVALID_ALLOW_NOT_DELETED',
      message: 'allowNotDeleted is only valid on a dry run; a live run always requires a deleted organization',
    };
  }
  return { ok: true, value: { tenantId, dryRun, allowNotDeleted: rawAllow === true } };
}

/**
 * The stores a tenant is erased from, in run order. Knowledgebase before
 * helpdesk (they share a database and kb tables cascade off helpdesk_brands);
 * auth and dashboard last (they are what identifies the organization to the
 * rest). The runner, the preview, the Admin app and the Dashboard modal all
 * read this one list.
 */
export const PURGE_STORES = ['knowledgebase', 'helpdesk', 'playbook', 'forms', 'stripe', 'auth', 'dashboard'] as const;
export type PurgeStore = (typeof PURGE_STORES)[number];

/** How long a deleted organization can be restored before its data is erased. */
export const DELETION_GRACE_DAYS = 30;

export type PurgeFailureKind = 'refused' | 'unreachable';

export interface ErasureRecordShape {
  status: 'running' | 'failed' | 'done';
  failedStore: string | null;
  failureKind: PurgeFailureKind | null;
  /** Stores with a step row (finished). */
  stepsDone: number;
  /** True if ANY attempt on this record ever ended 'unreachable' — a store may have committed. */
  everUnreachable: boolean;
}

/**
 * The one shape of purge record that provably erased nothing, and so may be
 * restored: failed at the FIRST store, the store itself having refused before
 * doing any work, no step recorded, and no earlier attempt that went silent
 * (an unreachable answer may have committed even if a later attempt was
 * refused). Dashboard's restore route and the Admin app's Restore button both
 * use this; a record is otherwise refused (`TENANT_PURGE_STARTED`).
 */
export function isRestorableErasure(record: ErasureRecordShape | null): boolean {
  if (!record) return true;
  return (
    record.status === 'failed' &&
    record.failedStore === PURGE_STORES[0] &&
    record.failureKind === 'refused' &&
    record.stepsDone === 0 &&
    !record.everUnreachable
  );
}
